import { Box, Button, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure, useTimeout } from '@mantine/hooks';
import { TalkingHeadActorEventIdn, TalkingHeadService } from '@newo/api-client';
import { ArrowRight, Info } from 'lucide-react';
import { type FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import InformationModal from '@/components/InformationModal';
import Title from '@/components/Title';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import { PATHS, runtimeConfig } from '@/constants';
import { useHostContext } from '@/context/HostContext.tsx';
import styles from '@/pages/AgentTraining/components/StepsContainer/components/WaitingPlayerScreen/WaitingPlayerScreen.module.css';
import { buildNavigatePath } from '@/routes.tsx';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { useAgentTraining } from '@/store/agent-training';
import useAppStore from '@/store/app.ts';
import { AgentTrainingStep } from '@/types/agent-training.ts';
import { cn } from '@/utils';
import { CREATOR_VIDEO_MAP_KEYS } from '@/utils/creatorVideoKeys';

const WaitingPlayerScreen: FC = () => {
	const trainingVideo = partnerMetadata.getCreatorVideoSource(
		CREATOR_VIDEO_MAP_KEYS.TRAINING_WAITING
	);
	const navigate = useNavigate();
	const { clientSecret, connectorIdn, externalId } = useAppStore(
		useShallow((state) => ({
			externalId: state.externalId,
			clientSecret: state.clientSecret,
			connectorIdn: state.connectorIdn
		}))
	);

	const { isMiniCreatorMode, isOverlayMode, isPageMode, localization } =
		useHostContext();

	const [isRendered, { open: openButton }] = useDisclosure(isMiniCreatorMode);
	useTimeout(openButton, 10000, {
		autoInvoke: !isMiniCreatorMode
	});
	const [isOpenModal, { open, close }] = useDisclosure(false);

	const {
		setTrainingStep,
		resetState,
		isContactFormAllowed,
		isCustomerFormAllowed,
		isNextButtonClicked,
		setIsNextButtonClicked
	} = useAgentTraining();

	const handleNext = useCallback(async () => {
		setIsNextButtonClicked();
		if (clientSecret && connectorIdn && externalId) {
			await TalkingHeadService.sendActorEvent(
				{
					client_secret: clientSecret,
					connector_idn: connectorIdn,
					external_id: externalId,
					event_idn: TalkingHeadActorEventIdn.AgentRequest,
					arguments: []
				},
				{
					...(runtimeConfig.isRemoteBuild ? {} : { skipAuth: true })
				}
			);
		}

		if (isCustomerFormAllowed) {
			setTrainingStep(AgentTrainingStep.CustomerCreation);
			return;
		}

		if (isContactFormAllowed) {
			setTrainingStep(AgentTrainingStep.ContactAgreement);
			return;
		}

		setTrainingStep(AgentTrainingStep.Loading);
	}, [
		clientSecret,
		connectorIdn,
		externalId,
		isCustomerFormAllowed,
		isContactFormAllowed,
		setIsNextButtonClicked,
		setTrainingStep
	]);

	const handleBack = () => {
		if (!runtimeConfig.isRemoteBuild) {
			navigate(buildNavigatePath(PATHS.creatorIndex));
		}

		agentTrainingRealtime.disconnect();
		resetState();
	};
	const handleVideoFinished = useCallback(() => {
		if (!isNextButtonClicked) {
			handleNext();
		}
	}, [handleNext, isNextButtonClicked]);

	return (
		<div className="h-full">
			<div
				className={cn('flex flex-col h-full', {
					'animate-fadeIn2': isPageMode
				})}
			>
				<div className="w-full relative flex flex-col justify-between h-full">
					<div
						className={cn('flex flex-col h-full transition-all duration-300', {
							'gap-[43px]': isPageMode,
							'gap-3 px-3 py-2': isMiniCreatorMode || isOverlayMode
						})}
					>
						<Title
							className={cn(
								'text-primary-900 w-full font-semibold m-0 [font-family:var(--font-headlines)] transition-all',
								{
									'!text-2xl sm:!text-3xl': isPageMode,
									'!text-sm opacity-90 !leading-tight': isMiniCreatorMode,
									'!text-lg !leading-tight': isOverlayMode
								}
							)}
							level={4}
							size="h5"
						>
							<span className="flex items-center gap-2">
								{localization.translate('creator', 'trainingIntroVideo.title')}
								<UnstyledButton
									className="opacity-60 hover:opacity-100 transition"
									onClick={open}
								>
									<Info size={isMiniCreatorMode ? 14 : 20} />
								</UnstyledButton>
							</span>
						</Title>

						<div
							className={cn('flex flex-col transition-all', {
								'gap-[18px] sm:gap-[24px]': isPageMode,
								'gap-2': isMiniCreatorMode
							})}
						>
							<Box
								className={cn('overflow-hidden w-full shadow-inner', {
									'rounded-[16px]': isPageMode,
									'rounded-[10px] ring-1 ring-white/10': isMiniCreatorMode
								})}
							>
								<VideoPlayer
									autoPlay
									onVideoFinished={handleVideoFinished}
									source={trainingVideo}
								/>
							</Box>

							{isPageMode && (
								<Text
									className="text-gray-600 [font-family:var(--font-body)]"
									size="xs"
								>
									{localization.translate('creator', 'watch_60_second_video')}
								</Text>
							)}
						</div>
					</div>
					<div className="flex w-full justify-between gap-3">
						{!partnerMetadata.isCreatorTrainingBackButtonHidden() && (
							<UnstyledButton
								className="text-[#495057] [font-family:var(--font-body)] text-[14px] font-normal leading-[21px] not-italic"
								onClick={handleBack}
							>
								{localization.translate('common', 'back')}
							</UnstyledButton>
						)}

						{isRendered ? (
							<Button
								className={cn(
									'group hover:bg-[var(--button-hover)] sm:bg-[var(--button-hover)] border hover:border-transparent',
									'hover:text-white text-sm font-medium flex w-full max-w-fit sm:text-white',
									'pr-[22px] !justify-center !items-center !gap-[10px] !rounded-[12px] ml-auto',
									// TODO remove max-height in scope of NPE-6751
									{
										[styles.animatedContainer]: isPageMode,
										'!text-xs max-h-[30px]': isMiniCreatorMode
									}
								)}
								classNames={{
									inner: '!w-full !flex !items-center !justify-center',
									label: '!w-full !flex !justify-center'
								}}
								onClick={handleNext}
								rightSection={
									<ArrowRight
										className="opacity-0 transition-all duration-500 group-hover:opacity-100"
										size={20}
									/>
								}
							>
								<span className="!pl-[20px] w-full transition-all duration-500 group-hover:-translate-x-2 [font-family:var(--font-body)]">
									{localization.translate(
										'creator',
										'proceed_to_agent_creation'
									)}
								</span>
							</Button>
						) : undefined}
					</div>
				</div>

				<InformationModal
					closeModal={close}
					isOpenedModal={isOpenModal}
					title={
						<h1 className={cn({ 'text-base': isMiniCreatorMode })}>
							{localization.rich(
								'creator',
								'trainingIntroVideo.information_modal.title',
								{
									br: () => <br />
								}
							)}
						</h1>
					}
				>
					<div>
						<p className={cn('mb-4', { 'text-sm': isMiniCreatorMode })}>
							{localization.translate(
								'creator',
								'trainingIntroVideo.information_modal.intro'
							)}
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							{localization.rich(
								'creator',
								'trainingIntroVideo.information_modal.current',
								{
									strong: (chunk) => <strong>{chunk}</strong>,
									br: () => <br />
								}
							)}
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							{localization.rich(
								'creator',
								'trainingIntroVideo.information_modal.next',
								{
									strong: (chunk) => <strong>{chunk}</strong>,
									br: () => <br />
								}
							)}
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							{localization.translate(
								'creator',
								'trainingIntroVideo.information_modal.phone'
							)}
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							{localization.translate(
								'creator',
								'trainingIntroVideo.information_modal.test'
							)}
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							{localization.translate(
								'creator',
								'trainingIntroVideo.information_modal.after'
							)}
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							<strong>
								{localization.translate(
									'creator',
									'trainingIntroVideo.information_modal.reminder'
								)}
							</strong>
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							<span className="block">
								{localization.translate(
									'creator',
									'trainingIntroVideo.information_modal.promo.line1'
								)}
							</span>
							<span className="block">
								{localization.translate(
									'creator',
									'trainingIntroVideo.information_modal.promo.line2'
								)}
							</span>
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							{localization.translate(
								'creator',
								'trainingIntroVideo.information_modal.consultant'
							)}
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							{localization.translate(
								'creator',
								'trainingIntroVideo.information_modal.consultant_help'
							)}
						</p>

						<p className={cn('mb-2', { 'text-sm': isMiniCreatorMode })}>
							{localization.translate(
								'creator',
								'trainingIntroVideo.information_modal.closing'
							)}
						</p>
					</div>
				</InformationModal>
			</div>
		</div>
	);
};

export default WaitingPlayerScreen;
